import { createFileRoute } from '@tanstack/react-router';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { CoordinatorDashboard } from '@/dashboard/CoordinatorDashboard';
import { PartnerDashboard } from '@/dashboard/PartnerDashboard';
import { StudentDashboard } from '@/dashboard/StudentDashboard';
import { MemberProfileView } from '@/profile/MemberProfileSections';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/_app/_users/members/$memberId/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { memberId } = Route.useParams();
  const { me, isStudent, isViewingOwnProfile } = useCurrentUser();

  // The branch below cannot be answered without `me`, and guessing costs a real
  // request: a student landing on their own dashboard would otherwise paint the
  // profile view and fetch that profile before swapping.
  if (!me) {
    return <LoadingSpinner centered />;
  }

  // Branch on who is displayed, not who is looking; otherwise a study helper
  // opening a student's page would see a profile built from their own identity.
  // `isStudent` is the viewer's type, which is right here because this arm only
  // runs when the two are the same person.
  if (isViewingOwnProfile(memberId) && isStudent) {
    return <StudentDashboard />;
  }

  // Follow-up coordinators' and partners' dashboards hang off this route rather
  // than a group: the follow-up group types were retired, so `/groups/{slug}/`
  // can't dispatch to one.
  //
  // Self-only, unlike legacy, where a member of a coordinator's group could
  // read its whole dashboard, addresses included. Everyone else, admins
  // included, gets `MemberProfileView`.
  if (isViewingOwnProfile(memberId)) {
    if (me.profile_type === 'FOLLOWUP_COORDINATOR') {
      return <CoordinatorDashboard />;
    }
    if (me.profile_type === 'FOLLOWUP_PARTNER') {
      return <PartnerDashboard />;
    }
  }

  // Every other member — study helper, admin, or anyone someone else is
  // viewing — gets the same server-driven profile the `/profile/` tab renders.
  return <MemberProfileView memberId={memberId} />;
}
